import { useQuery } from '@tanstack/react-query';
import { QUERY_CACHE_PROFILES, queryClientCacheTime, queryClientStaleTime } from '../queryConsts';

/**
 * Frequently used defaults for React Query (Overridable with reactQueryOptions).
 */
const defaultQueryOptions = {
    /**
     * If query should run automatically.
     */
    enabled: true,
    /**
     * How fresh the cache should be.
     */
    staleTime: queryClientStaleTime,
    /**
     * Cache will be kept if it's not needed for duration of time.
     */
    cacheTime: queryClientCacheTime,
    /**
     * If network gives hiccups. Override for special cases.
     */
    retry: false,
    /**
     * Exponential backoff logic.
     * @param {number} attemptIndex Times it's retrying.
     * @returns Time to delay the next retry.
     */
    retryDelay: (attemptIndex) => {
        return Math.min(1000 * Math.pow(2, attemptIndex), 30000)
    },
    /**
     * Prevents API SPAM.
     */
    refetchOnWindowFocus: false,
    /**
     * Tries to refetch if it were disconnected directly after connection is up again (if data is stale).
     */
    refetchOnReconnect: true,
    /**
     * Don't refresh on mount, only if it's needed.
     */
    refetchOnMount: false
};

/**
 * Provides the application's standard React Query implementation.
 *
 * Features:
 * - Shared default React Query configuration
 * - Predefined cache profiles (default behaviours)
 * - Optional query persistence
 * - Automatic retries with exponential backoff
 * - Loading and background fetching states
 * - Query invalidation support
 *
 * @param {Array} queryKey Unique cache key.
 * @param {Function} queryFn Async function returning the API result.
 * @param {object} [reactQueryOptions={}] Optional React Query configuration.
 * @param {object} [reactQueryOptions.cacheProfile=QUERY_CACHE_PROFILES.DEFAULT] Predefined cache behaviour (staleTime/cacheTime).
 * @param {boolean} [reactQueryOptions.persist=cacheProfile.persist] Saves in IndexedDB (override from QUERY_CACHE_PROFILES).
 * @returns {object} React Query result. { data, isLoading, isFetching, isError, error, refetch }
 */
export function useBaseReactQuery(queryKey, queryFn, reactQueryOptions = {}) {
    if (!queryKey) {
        throw new Error("useBaseReactQuery requires a query key.");
    }

    const {
        cacheProfile = QUERY_CACHE_PROFILES.DEFAULT,
        persist,
        ...queryOptions
    } = reactQueryOptions;

    const {
        persist: profilePersist,
        ...cacheProfileOptions
    } = cacheProfile;

    return useQuery({
        queryKey,
        queryFn,
        ...defaultQueryOptions,
        ...cacheProfileOptions,
        ...queryOptions,
        meta: {
            persist: persist ?? profilePersist
        }
    });
}
